import {DelegateRole, IParentUser} from '../server/IServerApi';

/**
 * Sub-logins: the client mirror of the server's UserUtil (com.bidmii.bmf.util.UserUtil), so the
 * apps never hand-roll "is this my company's?" or "may I do this?".
 *
 * THE MODEL
 * A "company" is a parent contractor account. A "sub-login" (delegate) is a user with a
 * parentUser and a delegateRole. The EFFECTIVE CONTRACTOR is the parentUser when there is one,
 * else the user themselves - and it is the effective contractor that owns the work: the server
 * stamps the COMPANY onto the ownership fields (RequestForProposal.awardedUserUid /
 * requestedUserUid, Conversation contractor, Message.user), never the individual who acted.
 * Which human acted stays on Auditable.createdBy and is a different axis - see ownsAsCompany.
 *
 * NO-OP GUARANTEE
 * parentUser and delegateRole are null/absent for 100% of the accounts that exist today, so
 * every function here collapses to exactly today's answer for a normal account:
 * getEffectiveContractorId -> user.id, getEffectiveContractorUid -> user.uid, and every
 * permission check -> true. Nothing about a normal contractor's experience may change.
 *
 * Every function tolerates a null/undefined user (a logged-out app is a normal state, not an
 * error) and reports the safe answer rather than throwing.
 */

/**
 * Sub-logins: a single capability a contractor account can exercise.
 * Mirrors UserUtil.Permission. Normal accounts hold every Permission.
 */
export type Permission =
  | 'CREATE_BID'
  | 'EDIT_QUOTE'
  | 'UPDATE_PROGRESS'
  | 'SEND_MESSAGE'
  | 'REQUEST_SITE_VISIT'
  | 'SUBMIT_REVIEW'
  | 'VIEW';

/**
 * The minimum shape these helpers read. Deliberately structural and all-optional rather than
 * the full IUser: an IUser satisfies it, a `$TSFixMe` user satisfies it, and a user rehydrated
 * from storage without the sub-login fields satisfies it. TypeScript's weak-type check still
 * rejects an object with none of these properties, so passing the wrong thing is caught.
 */
export interface ISubLoginUser {
  id?: number;
  uid?: string | null;
  name?: string | null;
  companyName?: string | null;
  parentUser?: IParentUser | null;
  delegateRole?: DelegateRole | null;
}

/** A user that may not be loaded/logged in yet - the normal case while an app is booting. */
export type MaybeUser = ISubLoginUser | null | undefined;

/**
 * Sub-logins: the ONE place a delegate role is mapped to what it may do - the client mirror of
 * UserUtil.ROLE_PERMISSIONS. A null delegateRole (every account today) is deliberately absent
 * from this map and is granted everything by hasPermission().
 */
const ROLE_PERMISSIONS: {[key in DelegateRole]: Permission[]} = {
  PROJECT_MANAGER: [
    'CREATE_BID',
    'EDIT_QUOTE',
    'UPDATE_PROGRESS',
    'SEND_MESSAGE',
    'REQUEST_SITE_VISIT',
    'SUBMIT_REVIEW',
    'VIEW',
  ],
  PROJECT_COORDINATOR: [
    'UPDATE_PROGRESS',
    'SEND_MESSAGE',
    'REQUEST_SITE_VISIT',
    'SUBMIT_REVIEW',
    'VIEW',
  ],
  READ_ONLY: ['VIEW'],
};

/**
 * Whether this login acts on behalf of a company account (i.e. it is a sub-login).
 * False for every normal standalone account (both fields are null for 100% of accounts today),
 * and false when no user is loaded.
 *
 * Either signal alone is enough, because either one alone can be missing on a real delegate:
 * parentUser is a lazy association the server degrades to null when it cannot resolve the parent,
 * while delegateRole is a plain enum column that always survives - so delegateRole is the durable
 * signal (see IUser.parentUser in IServerApi). Keying off parentUser alone would read a degraded
 * delegate as a normal account and march them through the contractor-application wizard that the
 * delegate skips exist to prevent, precisely when the data is already broken.
 *
 * This answers "is this a sub-login?" only. It does NOT promise the company resolved: when
 * parentUser is degraded to null, getEffectiveContractorId/Uid still fall back to the user
 * themselves.
 */
export function isDelegate(user: MaybeUser): boolean {
  return user != null && (user.parentUser != null || user.delegateRole != null);
}

/**
 * The id of the account that owns this user's work: the parent company for a delegate, the user
 * themselves otherwise. This is the id the server stamps on the ownership fields, so it is what
 * every "is this mine?" check must compare against.
 *
 * Null only when there is no user (or no id) to resolve - callers must treat null as "owns
 * nothing", never as a match. Prefer ownsAsCompany(), which handles that for you.
 */
export function getEffectiveContractorId(user: MaybeUser): number | null {
  return user?.parentUser?.id ?? user?.id ?? null;
}

/**
 * The uid of the account that owns this user's work - the uid axis of
 * getEffectiveContractorId(). Null when there is no user (or no uid) to resolve.
 */
export function getEffectiveContractorUid(user: MaybeUser): string | null {
  return user?.parentUser?.uid ?? user?.uid ?? null;
}

/**
 * The display name of the account that owns this user's work: the parent company's name for a
 * delegate, the user's own otherwise. Mirrors getEffectiveContractorId on the name axis - both
 * IUser.name and IParentUser.name are the server's getDisplayName() ("First L"), so a normal
 * account gets back exactly the user.name it renders today.
 *
 * For the company/brand name shown on a contractor's profile, use
 * getEffectiveContractorCompanyName() instead.
 */
export function getEffectiveContractorName(user: MaybeUser): string | null {
  return user?.parentUser?.name ?? user?.name ?? null;
}

/**
 * The company/brand name of the account that owns this user's work ("Priority Restoration"),
 * as opposed to the person's display name. Nullable: plenty of accounts have no companyName.
 * A normal account gets back exactly its own companyName, as today.
 */
export function getEffectiveContractorCompanyName(
  user: MaybeUser,
): string | null {
  return user?.parentUser?.companyName ?? user?.companyName ?? null;
}

/**
 * Whether the user's delegate role grants a capability - the client mirror of
 * UserUtil.hasPermission, and the single source of truth the other role checks are built on.
 *
 * A normal account (and the parent company account itself) has no delegateRole and keeps every
 * permission it has today, so this returns true for every account that exists right now.
 *
 * This is a UI gate only: it decides what to show/enable. The server enforces the same map
 * independently, so a stale or spoofed client cannot widen what a delegate may actually do.
 */
export function hasPermission(
  user: MaybeUser,
  permission: Permission,
): boolean {
  if (user == null || user.delegateRole == null) {
    return true;
  }
  if (permission == null) {
    return false;
  }
  const granted = ROLE_PERMISSIONS[user.delegateRole];
  return granted != null && granted.indexOf(permission) !== -1;
}

/**
 * Whether the user may create new bids. Normal accounts and PROJECT_MANAGER delegates can;
 * PROJECT_COORDINATOR and READ_ONLY delegates cannot.
 */
export function canCreateBids(user: MaybeUser): boolean {
  return hasPermission(user, 'CREATE_BID');
}

/**
 * Whether the user may make changes at all (message, update stages). Only READ_ONLY delegates
 * cannot.
 *
 * Deliberately coarse - it is the "can this account touch anything?" gate, mirroring the
 * server's UPDATE_PROGRESS permission. It is NOT a substitute for the specific capability:
 * a PROJECT_COORDINATOR passes canModify() but may NOT create a bid or edit a quote. Gate
 * those on canCreateBids() / hasPermission(user, 'EDIT_QUOTE') or the UI will offer an action
 * the server rejects.
 */
export function canModify(user: MaybeUser): boolean {
  return hasPermission(user, 'UPDATE_PROGRESS');
}

/** Whether the user is a view-only delegate. False for every normal standalone account. */
export function isReadOnly(user: MaybeUser): boolean {
  return user != null && user.delegateRole === 'READ_ONLY';
}

/**
 * The canonical "is this my company's?" check: whether the given owner id is the account this
 * user's work belongs to. A delegate, a sibling delegate and the company account itself all
 * resolve to the same company id, which is the id the server stamped on the record - so all
 * three agree, and a normal account compares its own id exactly as it does today.
 *
 * Compare against an OWNERSHIP field (a project's/quote's contractor, awardedUserUid's account,
 * a message's user). Do NOT compare against createdBy - that records which human acted, is a
 * different axis, and would answer "no" for a teammate's record that is genuinely the
 * company's. False whenever either side is missing.
 */
export function ownsAsCompany(
  user: MaybeUser,
  ownerId: number | null | undefined,
): boolean {
  const contractorId = getEffectiveContractorId(user);
  return contractorId != null && ownerId != null && contractorId === ownerId;
}
